// src/components/layout/Header.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import botablewhite from '../../images/botable-white.png';
import botableLogo from '../../images/Botable-Logo.png';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&display=swap');

  /* ── header slide-down ── */
  @keyframes header-in {
    from { opacity:0; transform: translateY(-100%); }
    to   { opacity:1; transform: translateY(0); }
  }
  .header-animate { animation: header-in .45s cubic-bezier(.22,.68,0,1.1) both; }

  /* ── logo hover ── */
  .logo-wrap { transition: transform .25s cubic-bezier(.34,1.56,.64,1), filter .25s ease; }
  .logo-wrap:hover { transform: scale(1.06); filter: drop-shadow(0 0 10px rgba(108,99,255,.45)); }

  /* ── nav pill active / hover ── */
  .hdr-link {
    position: relative;
    transition: color .2s ease;
  }
  .hdr-link::after {
    content: '';
    position: absolute;
    bottom: -3px; left: 50%; right: 50%;
    height: 2px;
    border-radius: 99px;
    background: linear-gradient(90deg, #6c63ff, #a78bfa);
    transition: left .25s ease, right .25s ease;
  }
  .hdr-link:hover::after { left: 0; right: 0; }

  /* ── dark-mode toggle ── */
  .toggle-track {
    position: relative;
    width: 44px; height: 24px;
    border-radius: 99px;
    cursor: pointer;
    transition: background .3s ease;
    flex-shrink: 0;
  }
  .toggle-thumb {
    position: absolute;
    top: 3px; left: 3px;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: transform .3s cubic-bezier(.34,1.56,.64,1), background .3s ease;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; line-height: 1;
    box-shadow: 0 1px 4px rgba(0,0,0,.25);
  }
  .toggle-thumb.on { transform: translateX(20px); }

  /* ── glass strip ── */
  .header-glass {
    backdrop-filter: blur(16px) saturate(180%);
    -webkit-backdrop-filter: blur(16px) saturate(180%);
  }

  /* ── mobile menu ── */
  @keyframes menu-drop {
    from { opacity:0; transform:translateY(-8px); }
    to   { opacity:1; transform:translateY(0); }
  }
  .menu-drop { animation: menu-drop .2s ease both; }
`;

const Header = ({ isDarkMode, toggleDarkMode }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <style>{STYLES}</style>

      <header
        className={`header-animate header-glass fixed top-0 left-0 w-full z-[100] transition-all duration-300 ${
          scrolled
            ? isDarkMode
              ? 'bg-[#0a0a1a]/90 border-b border-white/8 shadow-[0_4px_30px_rgba(0,0,0,.4)]'
              : 'bg-white/80 border-b border-gray-200/60 shadow-[0_4px_24px_rgba(108,99,255,.08)]'
            : isDarkMode
              ? 'bg-[#0a0a1a]/70 border-b border-white/5'
              : 'bg-white/60 border-b border-white/60'
        }`}
        style={{ height: '68px' }}
      >
        {/* top accent strip — 2px violet gradient */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-violet-500 via-indigo-400 to-blue-400" />

        <div className="flex items-center justify-between h-full px-6 md:px-10 max-w-screen-2xl mx-auto">

          {/* ── Logo ── */}
          <Link to="/" className="logo-wrap flex items-center gap-3 flex-shrink-0">
            <img
              src={isDarkMode ? botablewhite : botableLogo}
              alt="Botable Logo"
              className="h-9 w-auto"
            />
          </Link>

          {/* ── Desktop right side ── */}
          <div className="hidden md:flex items-center gap-6">

            {/* Nav links */}
            {[
              { to: '/',        label: 'Home'     },
              { to: '/signin',  label: 'Sign In'  },
              { to: '/signup',  label: 'Sign Up'  },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`hdr-link text-sm font-semibold pb-0.5 ${
                  isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {label}
              </Link>
            ))}

            {/* Divider */}
            <div className={`h-5 w-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />

            {/* Dark mode toggle */}
            {toggleDarkMode && (
              <button
                onClick={toggleDarkMode}
                aria-label="Toggle dark mode"
                className={`toggle-track border ${
                  isDarkMode
                    ? 'bg-violet-600/80 border-violet-500/50'
                    : 'bg-gray-200 border-gray-300'
                }`}
              >
                <span className={`toggle-thumb ${isDarkMode ? 'on' : ''}`}>
                  {isDarkMode ? '🌙' : '☀️'}
                </span>
              </button>
            )}

            {/* CTA */}
            <Link
              to="/signup"
              className="ml-1 inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] hover:from-[#5b52ee] hover:to-[#7c3aed] text-white text-sm font-bold transition-all duration-200 hover:scale-[1.05] active:scale-95 shadow-md hover:shadow-violet-500/30"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Get Started →
            </Link>
          </div>

          {/* ── Mobile: toggle + hamburger ── */}
          <div className="flex md:hidden items-center gap-3">
            {toggleDarkMode && (
              <button
                onClick={toggleDarkMode}
                aria-label="Toggle dark mode"
                className={`toggle-track border ${
                  isDarkMode ? 'bg-violet-600/80 border-violet-500/50' : 'bg-gray-200 border-gray-300'
                }`}
              >
                <span className={`toggle-thumb ${isDarkMode ? 'on' : ''}`}>
                  {isDarkMode ? '🌙' : '☀️'}
                </span>
              </button>
            )}
            <button
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Open menu"
              className={`w-9 h-9 flex flex-col justify-center items-center gap-[5px] rounded-xl transition-colors ${
                isDarkMode ? 'hover:bg-white/8' : 'hover:bg-gray-100'
              }`}
            >
              {[0,1,2].map(i => (
                <span
                  key={i}
                  className={`block h-[2px] rounded-full transition-all duration-300 ${
                    isDarkMode ? 'bg-gray-300' : 'bg-gray-600'
                  } ${
                    mobileOpen && i === 0 ? 'w-5 rotate-45 translate-y-[7px]'
                    : mobileOpen && i === 1 ? 'w-0 opacity-0'
                    : mobileOpen && i === 2 ? 'w-5 -rotate-45 -translate-y-[7px]'
                    : 'w-5'
                  }`}
                />
              ))}
            </button>
          </div>
        </div>

        {/* ── Mobile dropdown ── */}
        {mobileOpen && (
          <div
            className={`menu-drop md:hidden absolute top-full left-0 w-full border-t px-6 py-4 flex flex-col gap-3 ${
              isDarkMode
                ? 'bg-[#0f0f24]/95 border-white/8 backdrop-blur-xl'
                : 'bg-white/95 border-gray-200 backdrop-blur-xl shadow-xl'
            }`}
          >
            {[
              { to: '/',       label: '🏠 Home'    },
              { to: '/signin', label: '🔐 Sign In' },
              { to: '/signup', label: '✨ Sign Up'  },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`text-sm font-semibold py-2.5 px-4 rounded-xl transition-colors ${
                  isDarkMode
                    ? 'text-gray-300 hover:bg-white/8 hover:text-white'
                    : 'text-gray-700 hover:bg-violet-50 hover:text-violet-700'
                }`}
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {label}
              </Link>
            ))}
            <Link
              to="/signup"
              onClick={() => setMobileOpen(false)}
              className="mt-1 text-center py-3 px-4 rounded-xl bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] text-white text-sm font-bold"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Get Started →
            </Link>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;